import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  downloadImportLogFile,
  fetchImportLogs,
  type ImportLogStatus,
} from "@/services/affiniaUsersSsoConfigApi";

// Radix Select doesn't allow an empty-string value, so we represent "no
// filter" with a sentinel we translate to undefined at request time.
const ANY = "__any__";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: ImportLogStatus; label: string }[] = [
  { value: "success", label: "Success" },
  { value: "failure", label: "Failure" },
];

function statusVariant(
  status: ImportLogStatus,
): "default" | "destructive" {
  return status === "success" ? "default" : "destructive";
}

function statusLabel(status: ImportLogStatus): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AffiniaUserConfigImportLogTab() {
  const { toast } = useToast();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<ImportLogStatus | "">("");
  const [uploadedBy, setUploadedBy] = useState("");
  const [debouncedUploadedBy, setDebouncedUploadedBy] = useState("");

  // Debounce the email input so we don't fire a request per keystroke. 2s
  // matches the pattern UploadLogsTab uses. Also resets to page 0 so the
  // admin isn't stuck on page 3 of a filtered result set that only has 1.
  useEffect(() => {
    const trimmed = uploadedBy.trim();
    if (trimmed === debouncedUploadedBy) return;
    const handle = window.setTimeout(() => {
      setDebouncedUploadedBy(trimmed);
      setPage(0);
    }, 2000);
    return () => window.clearTimeout(handle);
  }, [uploadedBy, debouncedUploadedBy]);

  const params = useMemo(
    () => ({
      skip: page * pageSize,
      limit: pageSize,
      status: statusFilter || undefined,
      uploaded_by: debouncedUploadedBy || undefined,
    }),
    [page, pageSize, statusFilter, debouncedUploadedBy],
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["affinia-user-config-import-log", params],
    queryFn: () => fetchImportLogs(params),
    placeholderData: keepPreviousData,
  });

  const resetFilters = () => {
    setStatusFilter("");
    setUploadedBy("");
    setDebouncedUploadedBy("");
    setPage(0);
  };
  const hasActiveFilters = Boolean(statusFilter || uploadedBy);

  // Download mutation keyed on log id so the per-row spinner only shows on
  // the row the admin clicked, not every row in the table.
  const downloadMutation = useMutation({
    mutationFn: ({ id, filename }: { id: string; filename: string }) =>
      downloadImportLogFile(id, filename),
    onError: (err: Error) => {
      toast({
        title: "Download failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const downloadingId = downloadMutation.isPending
    ? downloadMutation.variables?.id ?? null
    : null;

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-border bg-card p-5 sm:p-6">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">Import history</h2>
            <p className="text-xs text-muted-foreground">
              Every attempt to bulk-import users to the Affinia SSO config. Click
              the download icon on a row to retrieve the uploaded file.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </header>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="import-log-status">Status</Label>
            <Select
              value={statusFilter || ANY}
              onValueChange={(v) => {
                setStatusFilter(v === ANY ? "" : (v as ImportLogStatus));
                setPage(0);
              }}
            >
              <SelectTrigger id="import-log-status">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any status</SelectItem>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="import-log-uploaded-by">Uploaded by</Label>
            <Input
              id="import-log-uploaded-by"
              type="email"
              placeholder="alice@example.com"
              value={uploadedBy}
              onChange={(e) => setUploadedBy(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            disabled={!hasActiveFilters}
          >
            Clear filters
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card">
        <div className="overflow-x-auto pr-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Uploaded at</TableHead>
                <TableHead>Uploaded by</TableHead>
                <TableHead>Filename</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Rows</TableHead>
                <TableHead className="w-[90px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Loading import logs…
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-destructive">
                    <AlertCircle className="mr-2 inline h-4 w-4" />
                    {(error as Error)?.message ?? "Failed to load import logs"}
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                    {hasActiveFilters
                      ? "No import attempts match the current filters."
                      : "No import attempts yet."}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDate(row.uploaded_at)}
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate" title={row.uploaded_by}>
                      {row.uploaded_by}
                    </TableCell>
                    <TableCell>
                      <span
                        className="block max-w-[220px] truncate"
                        title={row.filename}
                      >
                        {row.filename}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant={statusVariant(row.status)}
                          className="whitespace-nowrap"
                        >
                          {statusLabel(row.status)}
                        </Badge>
                        {row.error_message && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                aria-label="View error details"
                              >
                                <Info className="h-4 w-4" aria-hidden />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-96">
                              <p className="text-xs font-semibold text-foreground">
                                Error details
                              </p>
                              <p className="mt-1 whitespace-pre-wrap break-words text-xs text-muted-foreground">
                                {row.error_message}
                              </p>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {row.inserted_count > 0 && (
                          <Badge variant="default" className="whitespace-nowrap">
                            {row.inserted_count} added
                          </Badge>
                        )}
                        {row.skipped_count > 0 && (
                          <Badge variant="secondary" className="whitespace-nowrap">
                            {row.skipped_count} skipped
                          </Badge>
                        )}
                        {row.duplicate_count > 0 && (
                          <Badge variant="outline" className="whitespace-nowrap">
                            {row.duplicate_count} duplicate
                            {row.duplicate_count === 1 ? "" : "s"}
                          </Badge>
                        )}
                        {row.inserted_count === 0
                          && row.skipped_count === 0
                          && row.duplicate_count === 0 && (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          downloadMutation.mutate({
                            id: row.id,
                            filename: row.filename,
                          })
                        }
                        disabled={!row.has_file || downloadingId === row.id}
                        title={
                          row.has_file
                            ? "Download file"
                            : "No file in storage"
                        }
                        aria-label={
                          row.has_file
                            ? `Download ${row.filename}`
                            : `${row.filename} is not available`
                        }
                      >
                        {downloadingId === row.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <footer className="flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {total === 0
              ? "0 entries"
              : `Showing ${from}–${to} of ${total}`}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label
                htmlFor="import-log-page-size"
                className="whitespace-nowrap text-xs text-muted-foreground"
              >
                Rows per page
              </Label>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(0);
                }}
              >
                <SelectTrigger id="import-log-page-size" className="h-8 w-[72px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || isFetching}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Prev
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages || isFetching}
              >
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
}
